import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { GetNoteListPayload } from '@/domains/notes/store/types/noteTypes';
import { RootState } from '@/store/types/RootState';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsDynamicOverrideMode } from '@/features/eds/enums/EdsDynamicOverrideMode';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import { NoteSlots } from '@/domains/notes/enums/NoteSlots';
import { selectNoteList } from '@/domains/notes/store/selectors/noteSelectors';
import { setOptionsAreLoading } from '@/domains/notes/store/slices/createNoteSlice';
import {
  deleteNote,
  getEntitiesFromClaimSet,
  getEntitiesFromClaimSetSuccess,
  getNoteList,
  getNoteListSuccess,
  getNoteReminder,
  getNoteReminderSuccess,
  incrementNotePage,
  markNoteAsRead,
  markNoteAsUnread,
  refreshNote,
  refreshNoteSuccess,
  setNoteUpdateInProgress,
  startLoadingNoteListData,
} from '@/domains/notes/store/slices/noteSlice';
import { SortDirection } from '@/enums/SortDirection';
import { DocumentService } from '@/services/DocumentService/DocumentService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';

const NOTE_INCLUDES = [
  `${EdsEntityCodes.CreatedByPerson}.${EdsEntityCodes.Accounts}`,
  `${EdsEntityCodes.Reviewers}.${EdsEntityCodes.Account}.${EdsEntityCodes.Person}`,
  `${EdsEntityCodes.Documents}.${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.DocumentVersions}`,
].join(',');

export function* getNoteListSaga({ type, payload }: PayloadAction<GetNoteListPayload>) {
  try {
    yield put(startLoadingNoteListData());

    const currentClaimSetId = yield select(selectCurrentClaimSetId);
    const filters = [
      ...(payload?.filters || []),
      { values: [currentClaimSetId], propertyName: EdsFilterEntities.ClaimSetId },
    ];
    const pagination = yield select((state: RootState) => state.notes.data.pagination);
    const requestUrl = `/filter/${EdsEntityCodes.Note}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, sortBy: edsToEntityFormat('pinned'), sortDirection: SortDirection.DESC, includes: NOTE_INCLUDES })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters, {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      dynamicOverrideMode: EdsDynamicOverrideMode.Merge,
      section: EdsDefinitionSections.RHPanelNotes,
    });

    EdsDebugService.logResponse(EdsEntityCodes.Note, {
      rawResponse: data,
      normalizedResponse,
      section: EdsDefinitionSections.RHPanelNotes,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecordIncludingEntities<NoteSlots>(normalizedResponse[0], true)
        : {},
    });

    const transformedDataWithLatestVersion: NormalizedV2Response[] = yield call(
      Promise.all.bind(Promise),
      normalizedResponse.map(async (note, noteIndex) => {
        const noteDocumentEntities = await Promise.all(
          (note.entities.Documents as NormalizedV2Response[])?.map(async (normalizedItem, documentIndex) => {
            return await DocumentService.mergeLastInstanceVersionIntoDocumentFields(
              normalizedItem,
              data.listings[noteIndex].documents[documentIndex].documentInstances,
              EdsDefinitionSections.RHPanelNotes,
            );
          }) || [],
        );
        return { ...note, entities: { ...note.entities, Documents: noteDocumentEntities } };
      }),
    );

    if (data.listings?.length) {
      yield call(getNoteReminderSaga, { payload: data.listings.map((note) => note.id), type: '' });
    }

    yield put(
      getNoteListSuccess({
        shouldRewrite: type !== incrementNotePage.type,
        data: {
          listings: transformedDataWithLatestVersion,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Notes fetching error',
      content: 'Unable to fetch notes. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  }
}

export function* getEntitiesFromClaimSetSaga({ payload }: PayloadAction<string>) {
  try {
    yield put(setOptionsAreLoading(true));

    const { data } = yield call(
      HttpService.get,
      `/Note/available-entities/${payload}`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL,
      },
    );

    const entityOptions = data.map((item) => ({
      label: item.title,
      value: item.entityId,
      entityType: item.entityType,
    }));

    yield put(getEntitiesFromClaimSetSuccess(entityOptions));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setOptionsAreLoading(false));
  }
}

export function* refreshNoteSaga({ payload }: PayloadAction<number>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `/v2/Note/${payload}`,
      { includeEntities: NOTE_INCLUDES },
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );
    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data, {
      dynamicOverrideMode: EdsDynamicOverrideMode.Merge,
      section: EdsDefinitionSections.RHPanelNotes,
    });

    const notes = yield select(selectNoteList);

    if (notes?.length) {
      yield call(getNoteReminderSaga, { payload: notes.map((note) => note.id), type: '' });
    }

    yield put(refreshNoteSuccess(normalizedResponse));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Note refresh error',
      content: 'Unable to refresh the note. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  }
}

export function* getNoteReminderSaga({ payload }: PayloadAction<number[]>) {
  try {
    const requestUrl = `/filter/${EdsEntityCodes.Reminder}?${QueryParamsService.constructSearchParamsFromRecord({ includes: `${EdsEntityCodes.UsersToRemind}.${EdsEntityCodes.Account}`, perPage: 10000 })}`;
    const filters = [
      { propertyName: EdsFilterEntities.RelatedEntityKey, values: payload },
      { propertyName: EdsFilterEntities.RelatedEntityType, values: [EdsEntityCodes.Note] },
    ];

    const { data } = yield call(HttpService.post, requestUrl, filters, {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      section: EdsDefinitionSections.RHPanelNotes,
    });

    EdsDebugService.logResponse(EdsEntityCodes.Reminder, {
      rawResponse: data,
      normalizedResponse,
      section: EdsDefinitionSections.RHPanelNotes,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecord<NoteSlots>(normalizedResponse[0])
        : {},
    });

    yield put(
      getNoteReminderSuccess({
        reminders: {
          listings: normalizedResponse,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Get note reminder error',
      content: "Failed to get a note's reminder. Please try again.",
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  }
}

export function* markNoteAsReadSaga({ payload }: PayloadAction<number>) {
  try {
    yield put(setNoteUpdateInProgress({ noteId: payload, value: true }));

    yield call(HttpService.post, `/${EdsEntityCodes.Note}/review`, payload, {
      baseURL: import.meta.env.VITE_API_URL,
      headers: {
        'Content-Type': 'application/json-patch+json',
      },
    });
    yield call(refreshNoteSaga, { payload, type: '' });
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Note reviewing error',
      content: 'Unable to review the note. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setNoteUpdateInProgress({ noteId: payload, value: false }));
  }
}

export function* markNoteAsUnreadSaga({ payload }: PayloadAction<number>) {
  try {
    yield put(setNoteUpdateInProgress({ noteId: payload, value: true }));

    yield call(HttpService.post, `/${EdsEntityCodes.Note}/unreview`, payload, {
      baseURL: import.meta.env.VITE_API_URL,
      headers: {
        'Content-Type': 'application/json-patch+json',
      },
    });
    yield call(refreshNoteSaga, { payload, type: '' });
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Note unreviewing error',
      content: 'Unable to unreview the note. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } finally {
    yield put(setNoteUpdateInProgress({ noteId: payload, value: false }));
  }
}

function* deleteNoteSaga({ payload }: PayloadAction<number>) {
  try {
    const claimSetId = yield select(selectCurrentClaimSetId);

    yield call(HttpService.delete, `/${EdsEntityCodes.Note}/${payload}`, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    SnacksService.add({
      type: SnackType.Success,
      title: 'Note has been deleted successfully',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(getNoteList({ filters: [{ propertyName: 'ClaimSetId', values: [claimSetId] }] }));
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Note deletion error',
      content: 'Unable to delete the note. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  }
}

export function* notesWatcher() {
  yield takeLatest([getNoteList.type, incrementNotePage.type], getNoteListSaga);
  yield takeLatest([refreshNote.type], refreshNoteSaga);
  yield takeLatest([getEntitiesFromClaimSet.type], getEntitiesFromClaimSetSaga);
  yield takeLatest([getNoteReminder.type], getNoteReminderSaga);
  yield takeLatest([markNoteAsRead.type], markNoteAsReadSaga);
  yield takeLatest([markNoteAsUnread.type], markNoteAsUnreadSaga);
  yield takeLatest([deleteNote.type], deleteNoteSaga);
}
